import settings from './settings/settings.routes';
import conversation from './conversation/conversation.routes';
import { routes as searchRoutes } from '../../modules/search/search.routes';
import { routes as contactRoutes } from './contacts/routes';
import { routes as companyRoutes } from './companies/routes';
import { routes as notificationRoutes } from './notifications/routes';
import { routes as novidadesRoutes } from './novidades/routes';
import { routes as inboxRoutes } from './inbox/routes';
import { frontendURL } from '../../helper/URLHelper';
import helpcenterRoutes from './helpcenter/helpcenter.routes';
import campaignsRoutes from './campaigns/campaigns.routes';
import { routes as captainRoutes } from './captain/captain.routes';
import AppContainer from './Dashboard.vue';
import Suspended from './suspended/Index.vue';
import NoAccounts from './noAccounts/Index.vue';
import store from 'dashboard/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

// Advup: Appointments agenda
import appointmentsRoutes from './appointments/appointments.routes';
// Advup: Broadcasts (Disparador em Massa)
import broadcastsRoutes from './broadcasts/broadcasts.routes';
// Advup: Chat Interno
import internalChatRoutes from './internal-chat/internalChat.routes';

// 🔴 CADA TELA BAIXA QUANDO É ABERTA (06/10/2026, missão de velocidade).
// Estas telas eram importadas de forma estática: o pacote inicial levava a
// ficha do processo (17 mil linhas), o Financeiro, a Agenda, Minutas… para
// quem só ia abrir o Chat — 11 MB de JS e 7,7 MB de CSS antes da primeira
// tela. Agora cada uma é um pedaço próprio, e `preCarregarTelas.js` baixa as
// principais quando o navegador fica ocioso: a primeira tela chega antes e
// trocar de tela continua instantâneo. Ver docs/VELOCIDADE-2026-10-06.md.
// Advup: Análise Comercial IA
const AnaliseComerciallIndex = () => import('./analise-comercial/Index.vue');

// Advup: Módulo Jurídico
const LegalIndex = () => import('./legal/Index.vue');
const LegalShow = () => import('./legal/FichaDoProcesso.vue');
const LegalSettings = () => import('./legal/Settings.vue');
const LegalProcessesReports = () => import('./legal/ProcessesReports.vue');
const LegalDashboard = () => import('./legal/Dashboard.vue');
const ClientsIndex = () => import('./legal/ClientsIndex.vue');
const CasesIndex = () => import('./legal/CasesIndex.vue');
const PublicationsIndex = () => import('./legal/PublicationsIndex.vue');
const ClientShow = () => import('./legal/FichaDoCliente.vue');
const GedIndex = () => import('./legal/GedIndex.vue');
const AssinaturasIndex = () => import('./minutas/AssinaturasIndex.vue');
const MinutasIndex = () => import('./minutas/MinutasIndex.vue');
const MinutaPagina = () => import('./minutas/MinutaPagina.vue');
const ModelosIndex = () => import('./minutas/ModelosIndex.vue');
const ModeloPagina = () => import('./minutas/ModeloPagina.vue');
const ContratosIndex = () => import('./minutas/ContratosIndex.vue');
const DocumentBuilder = () => import('./contracts/DocumentBuilder.vue');
const FinancialIndex = () => import('./financial/Index.vue');
const CorreioIndex = () => import('./correio/CorreioIndex.vue');
const CentralIndex = () => import('./central/CentralIndex.vue');
const ObligationShow = () => import('./financial/ObligationShow.vue');

// Advup: Pipeline and Follow Up imports
const PipelineView = () => import('./pipeline/PipelineView.vue');
const UnifiedPipelineDashboard = () =>
  import('./pipeline/UnifiedPipelineDashboard.vue');

const FollowUpCadencesView = () =>
  import('./follow-ups/FollowUpCadencesView.vue');
const FollowUpCadenceOverviewView = () =>
  import('./follow-ups/FollowUpCadenceOverviewView.vue');
const ReguaDoFollowUp = () => import('./follow-ups/ReguaDoFollowUp.vue');
const AdvupLabsView = () => import('./pipeline/AdvupLabsView.vue');
const SystemHealthView = () => import('./system-health/SystemHealthView.vue');
const SequencesIndex = () => import('./pipeline/sequences/SequencesIndex.vue');
const SequenceForm = () => import('./pipeline/sequences/SequenceForm.vue');

// Guard factory that redirects to dashboard when a feature flag is disabled.
// The sidebar already hides the menu, but this prevents direct URL access.
// Must be async to ensure account features are loaded before checking the flag
// (on page refresh, the accounts store is empty until AppContainer mounts).
const buildFeatureGuard = flag => async (to, from, next) => {
  const { accountId } = to.params;

  if (!store.getters['accounts/getAccount'](accountId)?.features) {
    await store.dispatch('accounts/get');
  }

  const isEnabled = store.getters['accounts/isFeatureEnabledonAccount'](
    accountId,
    flag
  );
  if (isEnabled) {
    next();
  } else {
    next({ path: `/app/accounts/${accountId}/dashboard` });
  }
};

const pipelineFeatureGuard = buildFeatureGuard(FEATURE_FLAGS.PIPELINE_BOARD);
// Audit 2026-05-07 — Sequences agora é flag independente, gerenciável no
// SuperAdmin sem afetar o resto do Pipeline Pro.
const pipelineSequencesFeatureGuard = buildFeatureGuard(
  FEATURE_FLAGS.PIPELINE_SEQUENCES
);

export default {
  routes: [
    {
      path: frontendURL('accounts/:accountId'),
      component: AppContainer,
      children: [
        // Advup: Unified Pipeline Dashboard (new unified view)
        {
          path: frontendURL('accounts/:accountId/pipeline/dashboard'),
          name: 'pipeline_dashboard_unified',
          component: UnifiedPipelineDashboard,
          meta: {
            permissions: [
              'administrator',
              'agent',
              'pipeline_manage',
              'pipeline_view',
            ],
            featureFlag: 'pipeline_board',
          },
          beforeEnter: pipelineFeatureGuard,
        },
        // Advup: Pipeline analytics - redirects to unified dashboard with pipeline=all
        {
          path: frontendURL('accounts/:accountId/pipeline/analytics'),
          name: 'pipeline_analytics',
          redirect: to => ({
            name: 'pipeline_dashboard_unified',
            params: { accountId: to.params.accountId },
            query: { pipeline: 'all' },
          }),
        },
        // Advup: Pipeline routes
        {
          path: frontendURL('accounts/:accountId/pipeline'),
          name: 'pipeline_index',
          component: PipelineView,
          meta: {
            permissions: [
              'administrator',
              'agent',
              'pipeline_manage',
              'pipeline_view',
            ],
            featureFlag: 'pipeline_board',
          },
          beforeEnter: pipelineFeatureGuard,
        },
        // Card-4.10 (2026-04-29) — admin de Sequence Definitions.
        // ATENÇÃO: declarado ANTES de `pipeline_with_funnel` porque
        // ambos casam /pipeline/<slug>; rotas mais específicas primeiro.
        // Audit 2026-05-07 — gating mudou de pipeline_board para
        // pipeline_sequences (flag independente, gerenciável no SuperAdmin).
        {
          path: frontendURL('accounts/:accountId/pipeline/sequences'),
          name: 'pipeline_sequences',
          component: SequencesIndex,
          meta: {
            permissions: ['administrator', 'agent', 'pipeline_manage'],
            featureFlag: 'pipeline_sequences',
          },
          beforeEnter: pipelineSequencesFeatureGuard,
        },
        {
          path: frontendURL('accounts/:accountId/pipeline/sequences/new'),
          name: 'pipeline_sequence_new',
          component: SequenceForm,
          meta: {
            permissions: ['administrator', 'agent', 'pipeline_manage'],
            featureFlag: 'pipeline_sequences',
          },
          beforeEnter: pipelineSequencesFeatureGuard,
        },
        {
          path: frontendURL('accounts/:accountId/pipeline/sequences/:id/edit'),
          name: 'pipeline_sequence_edit',
          component: SequenceForm,
          meta: {
            permissions: ['administrator', 'agent', 'pipeline_manage'],
            featureFlag: 'pipeline_sequences',
          },
          beforeEnter: pipelineSequencesFeatureGuard,
        },
        {
          path: frontendURL('accounts/:accountId/pipeline/:pipelineSlug'),
          name: 'pipeline_with_funnel',
          component: PipelineView,
          meta: {
            permissions: [
              'administrator',
              'agent',
              'pipeline_manage',
              'pipeline_view',
            ],
            featureFlag: 'pipeline_board',
          },
          beforeEnter: pipelineFeatureGuard,
        },
        {
          path: frontendURL('accounts/:accountId/pipeline/:pipelineSlug/:view'),
          name: 'pipeline_board',
          component: PipelineView,
          meta: {
            permissions: [
              'administrator',
              'agent',
              'pipeline_manage',
              'pipeline_view',
            ],
            featureFlag: 'pipeline_board',
          },
          beforeEnter: pipelineFeatureGuard,
        },
        {
          path: frontendURL(
            'accounts/:accountId/pipeline/:pipelineSlug/:view/:itemId'
          ),
          name: 'pipeline_item_detail',
          component: PipelineView,
          meta: {
            permissions: [
              'administrator',
              'agent',
              'pipeline_manage',
              'pipeline_view',
            ],
            featureFlag: 'pipeline_board',
          },
          beforeEnter: pipelineFeatureGuard,
        },
        // Advup: Follow Up routes
        // /follow-ups é o pouso do ícone de nav — pedido do Victor pra abrir
        // direto no Painel de aprovação, não na fila de lembretes antiga.
        {
          path: frontendURL('accounts/:accountId/follow-ups'),
          name: 'follow_ups',
          component: FollowUpCadencesView,
          meta: {
            permissions: ['administrator'],
            featureFlag: 'follow_ups',
          },
        },
        {
          path: frontendURL('accounts/:accountId/follow-ups/reminders'),
          name: 'follow_up_reminders',
          component: FollowUpCadenceOverviewView,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'follow_ups',
          },
        },
        // Sprint 3 — motor de cadência (FollowUpCadence*), o painel de gestão. As telas do
        // follow-up antigo (modelos, automações) saíram na F1 do follow-up como produto.
        // A régua como produto (P2, 18/10/2026): tela e endereço próprios — `nova` cria.
        {
          path: frontendURL(
            'accounts/:accountId/follow-ups/reguas/:definitionId'
          ),
          name: 'follow_up_regua',
          component: ReguaDoFollowUp,
          meta: {
            permissions: ['administrator'],
            featureFlag: 'follow_ups',
          },
        },
        {
          path: frontendURL('accounts/:accountId/follow-ups/cadences'),
          name: 'follow_up_cadences',
          component: FollowUpCadencesView,
          meta: {
            permissions: ['administrator'],
            featureFlag: 'follow_ups',
          },
        },
        // advup — dashboard de saúde do sistema + auditoria de incidentes
        // (pedido do Victor 2026-07-22 pós-incidente de mensagem perdida).
        {
          path: frontendURL('accounts/:accountId/system-health'),
          name: 'system_health',
          component: SystemHealthView,
          meta: {
            permissions: ['administrator'],
          },
        },
        // Advup: AdvupLabs flow builder
        {
          path: frontendURL('accounts/:accountId/advup-labs'),
          name: 'advup_labs_index',
          component: AdvupLabsView,
          meta: {
            permissions: ['administrator'],
            featureFlag: 'advup_labs',
          },
        },
        // Advup: Análise Comercial IA
        {
          path: frontendURL('accounts/:accountId/analise-comercial'),
          name: 'analise_comercial_index',
          component: AnaliseComerciallIndex,
          meta: {
            permissions: ['administrator'],
            featureFlag: 'analise_comercial',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.ANALISE_COMERCIAL),
        },
        // Advup: Módulo Jurídico
        {
          path: frontendURL('accounts/:accountId/processos/dashboard'),
          name: 'legal_dashboard',
          component: LegalDashboard,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'legal_module',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.LEGAL_MODULE),
        },
        // O LABORATÓRIO DE DESIGN (Painel 2027 e Mesa) está OCULTO desde
        // 02/10/2026 — estudo futuro. As rotas moram em
        // `legal/painel27/rotas.js`; para religar, importe e espalhe aqui.
        {
          path: frontendURL('accounts/:accountId/processos'),
          name: 'legal_processes_index',
          component: LegalIndex,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'legal_module',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.LEGAL_MODULE),
        },
        {
          path: frontendURL('accounts/:accountId/processos/:processId/:aba?'),
          name: 'legal_process_show',
          component: LegalShow,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'legal_module',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.LEGAL_MODULE),
        },
        {
          path: frontendURL('accounts/:accountId/processos/relatorios'),
          name: 'legal_processes_reports',
          component: LegalProcessesReports,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'legal_module',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.LEGAL_MODULE),
        },
        {
          path: frontendURL('accounts/:accountId/processos/configuracoes'),
          name: 'legal_settings',
          component: LegalSettings,
          meta: {
            permissions: ['administrator'],
            featureFlag: 'legal_module',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.LEGAL_MODULE),
        },
        // Advup: Módulo Jurídico — Cliente (Sub-fase 3.1)
        {
          path: frontendURL('accounts/:accountId/clientes'),
          name: 'legal_clients_index',
          component: ClientsIndex,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'legal_clients',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.LEGAL_CLIENTS),
        },
        {
          path: frontendURL('accounts/:accountId/clientes/:clientId/:aba?'),
          name: 'legal_client_show',
          component: ClientShow,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'legal_clients',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.LEGAL_CLIENTS),
        },
        // Advup: Casos — agrupador de Processos relacionados
        {
          path: frontendURL('accounts/:accountId/casos'),
          name: 'legal_cases_index',
          component: CasesIndex,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'legal_cases',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.LEGAL_CASES),
        },
        // Advup: Publicações do diário oficial (P1 da paridade Legal One)
        {
          path: frontendURL('accounts/:accountId/publicacoes'),
          name: 'legal_publications_index',
          component: PublicationsIndex,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'legal_publications',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.LEGAL_PUBLICATIONS),
        },
        // Advup: Módulo Jurídico — GED central (Sub-fase 4.2)
        {
          path: frontendURL('accounts/:accountId/ged'),
          name: 'legal_ged_index',
          component: GedIndex,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'legal_ged_central',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.LEGAL_GED_CENTRAL),
        },
        // Advup: módulo Minutas (29/09/2026) — o editor (Fase 3), Assinaturas e
        // Contratos (Fase 2).
        {
          path: frontendURL('accounts/:accountId/minutas'),
          name: 'minutas_index',
          component: MinutasIndex,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'contracts',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.CONTRACTS),
        },
        {
          path: frontendURL('accounts/:accountId/minutas/documento/:minutaId'),
          name: 'minutas_minuta',
          component: MinutaPagina,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'contracts',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.CONTRACTS),
        },
        // Modelos (Fase 4): a biblioteca e a página de um modelo no editor.
        {
          path: frontendURL('accounts/:accountId/minutas/modelos'),
          name: 'minutas_modelos',
          component: ModelosIndex,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'contracts',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.CONTRACTS),
        },
        {
          path: frontendURL('accounts/:accountId/minutas/modelos/:modeloId'),
          name: 'minutas_modelo',
          component: ModeloPagina,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'contracts',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.CONTRACTS),
        },
        {
          path: frontendURL('accounts/:accountId/minutas/assinaturas'),
          name: 'minutas_assinaturas',
          component: AssinaturasIndex,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'contracts',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.CONTRACTS),
        },
        {
          path: frontendURL('accounts/:accountId/minutas/contratos'),
          name: 'legal_contracts_index',
          component: ContratosIndex,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'contracts',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.CONTRACTS),
        },
        // O endereço antigo de Contratos continua valendo (favoritos, links).
        {
          path: frontendURL('accounts/:accountId/contratos'),
          name: 'legal_contracts_antigo',
          redirect: to => ({
            name: 'legal_contracts_index',
            params: to.params,
            query: to.query,
          }),
        },
        // Advup: Contratos — a tela ANTIGA (08/10/2026). Ficou "até a Fase 2
        // ser aprovada"; a Fase 2 (Contratos em Minutas) está no ar desde
        // 05/10 e a antiga já lia uma API que mudou: números vazios e tipos e
        // situações como chaves cruas ("CONTRACTS.TYPE.undefined"). Sem link
        // em lugar nenhum. O endereço antigo leva ao Contratos atual.
        {
          path: frontendURL('accounts/:accountId/contratos-antigo'),
          name: 'legal_contracts_legado',
          redirect: to => ({
            name: 'legal_contracts_index',
            params: { accountId: to.params.accountId },
          }),
        },
        // Advup: construtor de documentos — vive DENTRO de Contratos, por
        // decisão do Victor ("no momento não misture as coisas").
        {
          path: frontendURL('accounts/:accountId/contratos/gerar-documento'),
          name: 'legal_document_builder',
          component: DocumentBuilder,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'contracts',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.CONTRACTS),
        },
        // Advup: Correio — a caixa de e-mail do escritório (docs/email/TELA.md).
        // Administrador e agente: cada um vê só a própria caixa (o servidor garante).
        {
          path: frontendURL('accounts/:accountId/correio'),
          name: 'correio_index',
          component: CorreioIndex,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'correio',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.CORREIO),
        },
        // Advup: Central telefônica (telefonia nativa, etapa F) — a equipe inteira
        {
          path: frontendURL('accounts/:accountId/central-telefonica'),
          name: 'central_telefonica_index',
          component: CentralIndex,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'telephony',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.TELEPHONY),
        },
        // Advup: Financeiro
        {
          path: frontendURL('accounts/:accountId/financeiro'),
          name: 'financial_index',
          component: FinancialIndex,
          meta: {
            permissions: ['administrator'],
            featureFlag: 'financial_module',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.FINANCIAL_MODULE),
        },
        // Advup: Financeiro — ficha da obrigação (sub-fase FIN-1 do roadmap
        // de paridade Legal One, ver .claude/specs/plans/paridade-legalone-financeiro.md)
        {
          path: frontendURL(
            'accounts/:accountId/financeiro/obrigacoes/:obligationId/:aba?'
          ),
          name: 'financial_obligation_show',
          component: ObligationShow,
          meta: {
            permissions: ['administrator'],
            featureFlag: 'financial_module',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.FINANCIAL_MODULE),
        },
        // Advup: Menu Apps iframe viewer
        {
          path: frontendURL('accounts/:accountId/menu-apps/:appId'),
          name: 'menu_app_view',
          component: () => import('./MenuAppView.vue'),
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'menu_apps',
          },
          beforeEnter: buildFeatureGuard(FEATURE_FLAGS.MENU_APPS),
        },
        ...captainRoutes,
        ...inboxRoutes,
        ...conversation.routes,
        ...settings.routes,
        ...contactRoutes,
        ...companyRoutes,
        ...searchRoutes,
        ...notificationRoutes,
        ...novidadesRoutes,
        ...helpcenterRoutes.routes,
        ...campaignsRoutes.routes,
        // Advup: Appointments agenda (F2)
        ...appointmentsRoutes.routes,
        // Advup: Broadcasts (Disparador em Massa)
        ...broadcastsRoutes.routes,
        // Advup: Chat Interno
        ...internalChatRoutes.routes,
      ],
    },
    {
      path: frontendURL('accounts/:accountId/suspended'),
      name: 'account_suspended',
      meta: {
        permissions: ['administrator', 'agent', 'custom_role'],
      },
      component: Suspended,
    },
    {
      path: frontendURL('no-accounts'),
      name: 'no_accounts',
      component: NoAccounts,
    },
  ],
};
